import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { authAPI } from '../services/api';
import { getUserFriendlyError } from '../utils/errorMessages';
import { TimezoneService, isNativePlatform, getPlatform } from '../services/capacitor';
import { notificationService } from '../services/notificationService';

const AuthContext = createContext(null);

const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';
const APP_URL = window.location.origin;

// Get user's timezone with platform info
const getUserTimezone = () => {
  const timezone = TimezoneService.getDeviceTimezone();
  const platform = getPlatform();
  const isNative = isNativePlatform();
  console.log(`📍 Timezone: ${timezone} | Platform: ${platform} | Native: ${isNative}`);
  return timezone;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userTimezone] = useState(getUserTimezone());

  useEffect(() => {
    const initializeAuth = async () => {
      // PRIORITY 1: Check if we have a session_id in URL (hash or query parameter)
      const hash = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      
      // Try to get session_id from hash fragment first, then query params
      let sessionId = hashParams.get('session_id') || urlParams.get('session_id');
      
      // Also try the format #session_id=xxx (without ?)
      if (!sessionId && hash && hash.includes('session_id=')) {
        sessionId = hash.split('session_id=')[1].split('&')[0];
      }
      
      if (sessionId) {
        console.log('🔑 Found session_id, attempting exchange...');
        
        // Clean URL immediately to prevent reprocessing
        window.history.replaceState(null, '', window.location.pathname);
        
        try {
          // Exchange session_id for user data via backend
          console.log('📡 Calling backend exchange-session...');
          const response = await authAPI.exchangeSession(sessionId);
          
          console.log('✅ Exchange response:', response.data);
          
          if (response.data.success) {
            setUser(response.data.user);
            setIsAuthenticated(true);
            setLoading(false);
            
            console.log('✅ Auth successful! Redirecting to /home...');
            
            // 🔔 AUTO-SUBSCRIBE: For OAuth login, auto-subscribe to notifications
            setTimeout(async () => {
              try {
                console.log('🔔 OAuth login: Initiating automatic push notification subscription...');
                const subscribeResult = await notificationService.autoSubscribeAfterLogin(
                  process.env.REACT_APP_BACKEND_URL
                );
                console.log('🔔 OAuth auto-subscribe result:', subscribeResult);
              } catch (notifError) {
                console.warn('OAuth auto-subscribe warning:', notifError);
              }
            }, 1500);
            
            // Redirect to home after successful authentication
            window.location.href = '/home';
            return;
          } else {
            console.error('❌ Exchange failed: success=false');
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('❌ Session exchange error:', error);
          console.error('Error details:', error.response?.data);
          setIsAuthenticated(false);
        }
        
        setLoading(false);
        return;
      }
      
      // PRIORITY 2: Check for existing session via API call ONLY
      // Removed localStorage fallback as it was causing infinite redirect loops
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
        
        // Sync timezone on app open (handles travel within US or internationally)
        const currentTimezone = getUserTimezone();
        const userSavedTimezone = response.data?.preferences?.timezone;
        
        if (currentTimezone && currentTimezone !== userSavedTimezone) {
          console.log(`🌍 Timezone changed: ${userSavedTimezone} → ${currentTimezone}`);
          try {
            await axios.put(
              `${process.env.REACT_APP_BACKEND_URL}/api/user/timezone`,
              { timezone: currentTimezone },
              { withCredentials: true }
            );
            console.log('✅ Timezone synced automatically');
          } catch (tzErr) {
            console.warn('Could not sync timezone:', tzErr);
          }
        }
        
        // AUTO-FIX: Verify push subscription exists if notifications are enabled
        const notificationsEnabled = response.data?.preferences?.notifications_enabled;
        if (notificationsEnabled && 'serviceWorker' in navigator && 'PushManager' in window) {
          try {
            // Check if backend has our subscription
            const subCheckRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/push/status`,
              { credentials: 'include' }
            );
            
            if (subCheckRes.ok) {
              const subStatus = await subCheckRes.json();
              
              // If backend says no subscription but user has notifications enabled, try to resubscribe
              if (!subStatus.subscribed && Notification.permission === 'granted') {
                console.log('🔔 Auto-fixing missing push subscription...');
                
                const { notificationService } = await import('../services/notificationService');
                try {
                  await notificationService.subscribeToPush(process.env.REACT_APP_BACKEND_URL);
                  console.log('✅ Push subscription auto-fixed!');
                } catch (subErr) {
                  console.warn('⚠️ Auto-fix push subscription failed:', subErr.message);
                }
              }
            }
          } catch (pushCheckErr) {
            console.warn('Could not verify push subscription:', pushCheckErr);
          }
        }
      } catch (error) {
        // No valid session - user needs to login
        console.log('No valid session found');
        setIsAuthenticated(false);
        // Clear any stale localStorage data
        localStorage.removeItem('lifeflo_user');
        localStorage.removeItem('lifeflo_auth_time');
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const loginWithGoogle = () => {
    // Redirect to Emergent OAuth with callback to /home
    const redirectUrl = `${APP_URL}/home`;
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const login = async (email, password) => {
    try {
      // Use axios instead of fetch to avoid body stream issues with platform scripts
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        { email, password },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = response.data;

      // Store session token for API requests
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      
      // Store user data as backup for mobile browsers with cookie issues
      localStorage.setItem('lifeflo_user', JSON.stringify(data.user));
      localStorage.setItem('lifeflo_auth_time', Date.now().toString());
      
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Auto-detect and update user's timezone
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/timezone`,
          { timezone: userTimezone },
          { withCredentials: true }
        );
        console.log('Timezone updated:', userTimezone);
      } catch (tzError) {
        console.warn('Could not update timezone:', tzError);
      }
      
      // Fetch and apply user's saved language preference
      try {
        const localeResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/locale`,
          { withCredentials: true }
        );
        if (localeResponse.data?.locale?.language) {
          const userLang = localeResponse.data.locale.language;
          const userCountry = localeResponse.data.locale.country_code || 'US';
          localStorage.setItem('lifeflo_language', userLang);
          localStorage.setItem('lifeflo_country', userCountry);
          localStorage.setItem('lifeflo_locale_set', 'true');
          console.log('User language loaded:', userLang);
          // Dispatch event so LocaleContext can reload
          window.dispatchEvent(new CustomEvent('lifeflo-locale-updated', { 
            detail: { language: userLang, country: userCountry }
          }));
        }
      } catch (langError) {
        console.warn('Could not load user language:', langError);
      }
      
      // 🔔 AUTO-SUBSCRIBE: Automatically subscribe to push notifications after login
      // This runs in the background and doesn't block the login flow
      setTimeout(async () => {
        try {
          console.log('🔔 Initiating automatic push notification subscription...');
          const subscribeResult = await notificationService.autoSubscribeAfterLogin(
            process.env.REACT_APP_BACKEND_URL
          );
          console.log('🔔 Auto-subscribe result:', subscribeResult);
          
          // Dispatch event for components to react
          window.dispatchEvent(new CustomEvent('lifeflo-notification-status', {
            detail: subscribeResult
          }));
        } catch (notifError) {
          console.warn('Auto-subscribe warning:', notifError);
        }
      }, 1000); // Slight delay to ensure login completes first
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      
      // Extract user-friendly error message
      let errorMessage;
      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.detail || error.response.data?.message;
        
        if (status === 401) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (serverMessage && serverMessage.length < 100) {
          errorMessage = serverMessage;
        } else {
          errorMessage = 'Unable to sign in. Please try again.';
        }
      } else if (error.message === 'Network Error') {
        errorMessage = 'Unable to connect. Please check your internet connection.';
      } else {
        errorMessage = 'Unable to sign in. Please try again.';
      }
      
      throw new Error(errorMessage);
    }
  };

  const signup = async (userData) => {
    console.log('📝 Starting signup process...');
    
    // Add browser timezone to signup data
    const signupData = {
      ...userData,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    };
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(signupData)
      });

      console.log('📝 Signup response status:', response.status);

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('❌ Failed to parse signup response:', parseError);
        throw new Error('Server error. Please check your connection and try again.');
      }

      if (!response.ok) {
        console.error('❌ Signup failed:', data);
        throw new Error(data.detail || 'Unable to create account. Please try again.');
      }

      // Verify we got user data back
      if (!data.user || !data.user.id) {
        console.error('❌ Signup response missing user data:', data);
        throw new Error('Account creation incomplete. Please try again.');
      }

      // Success - store user data
      console.log('✅ Signup successful, storing user data...');
      localStorage.setItem('lifeflo_user', JSON.stringify(data.user));
      localStorage.setItem('lifeflo_auth_time', Date.now().toString());
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Auto-detect and update user's timezone after signup
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/timezone`,
          { timezone: userTimezone },
          { withCredentials: true }
        );
        console.log('✅ Timezone set on signup:', userTimezone);
      } catch (tzError) {
        console.warn('Could not set timezone:', tzError);
      }
      
      // 🔔 AUTO-SUBSCRIBE: Automatically subscribe new users to push notifications
      setTimeout(async () => {
        try {
          console.log('🔔 Initiating automatic push notification subscription for new user...');
          const subscribeResult = await notificationService.autoSubscribeAfterLogin(
            process.env.REACT_APP_BACKEND_URL
          );
          console.log('🔔 New user auto-subscribe result:', subscribeResult);
          
          // Dispatch event for components to react
          window.dispatchEvent(new CustomEvent('lifeflo-notification-status', {
            detail: subscribeResult
          }));
        } catch (notifError) {
          console.warn('New user auto-subscribe warning:', notifError);
        }
      }, 1000);
      
      console.log('✅ User authenticated:', data.user.email);
      return { success: true };
    } catch (error) {
      console.error('❌ Signup error:', error);
      
      // Check for network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection.');
      }
      
      throw new Error(error.message || 'Unable to create account. Please try again.');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      await authAPI.updateProfile(profileData);
      
      // Update local user state
      setUser(prev => ({
        ...prev,
        preferences: profileData
      }));
      
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    // Refresh user data from backend (useful after payment)
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      console.error('Refresh user error:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      // Unsubscribe from browser push notifications before logout
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            await subscription.unsubscribe();
            console.log('🔕 Browser push subscription cleared on logout');
          }
        } catch (pushError) {
          console.log('Push unsubscribe skipped:', pushError);
        }
      }
      
      // Call backend logout (which also deletes DB push subscription)
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage backup
      localStorage.removeItem('lifeflo_user');
      localStorage.removeItem('lifeflo_auth_time');
      localStorage.removeItem('lifeflo_push_subscribed'); // Clear push state flag
      
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Update timezone on backend when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      // Send timezone to backend
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/timezone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ timezone: userTimezone })
      }).catch(err => console.log('Timezone update skipped:', err));
    }
  }, [isAuthenticated, user, userTimezone]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        userTimezone,
        login,
        loginWithGoogle,
        signup,
        updateProfile,
        refreshUser,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
